import { useDebounce, useGetPlaces } from "@api";
import type { IPlace } from "@bbj-types/address";
import { type AutocompleteInputChangeReason, Typography } from "@mui/material";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { AutocompleteChips, type IAutocompleteChipsProps } from "../AutocompleteChips";

type IPlacesProps<DisableClearable extends boolean | undefined> = Omit<
  IAutocompleteChipsProps<IPlace, DisableClearable>,
  "label" | "getOptionLabel" | "isOptionEqualToValue" | "options" | "loading" | "onDeletePredicate" | "noOptionsText"
>;

const timeout = 1000;

export function Places<DisableClearable extends boolean | undefined = undefined>({
  onInputChange,
  placeholder = "Saisissez une ville, un département, une région",
  ...props
}: IPlacesProps<DisableClearable>) {
  const [options, setOptions] = useState<IPlace[]>([]);
  const [{ loading: apiHookLoading, data: apiHookResult }, getApiHookResult] = useGetPlaces();

  useEffect(() => {
    if (apiHookResult?.data) {
      setOptions(apiHookResult.data);
    }
  }, [apiHookResult]);

  //update option list if inputValue has changed since 1 sec
  const debounce = useDebounce(
    (newInputValue: string) => {
      if (newInputValue === "") {
        setOptions([]);
      } else if (getApiHookResult) {
        void getApiHookResult({ search: newInputValue });
      }
    },
    timeout,
    [getApiHookResult]
  );

  const handleInputChange = useCallback(
    (event: React.SyntheticEvent<Element, Event>, newInputValue: string, reason: AutocompleteInputChangeReason) => {
      if (onInputChange) {
        onInputChange(event, newInputValue, reason);
      }
      debounce(newInputValue);
    },
    [debounce, onInputChange]
  );

  const getOptionLabel = (option: IPlace) => {
    const { placeLabel, postalCodes } = option;
    return [placeLabel, postalCodes ? postalCodes[0] : undefined].filter(Boolean).join(" - ");
  };

  return (
    <AutocompleteChips
      placeholder={placeholder}
      loading={apiHookLoading}
      options={options}
      onInputChange={handleInputChange}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option: IPlace, value: IPlace) => option.placeLabel === value.placeLabel}
      noOptionsText={(inputValue) =>
        inputValue.length === 0 ? (
          <Typography variant="body1" color="textSecondary">
            Pas de résultat. Saisissez au moins 1 caractère pour avoir des propositions.
          </Typography>
        ) : (
          <Typography variant="body1" color="textSecondary">
            Pas de résultat
          </Typography>
        )
      }
      {...props}
    />
  );
}
